// TelcoTrack — zásoby: vnořené rozbalení produkt → šarže → kusy

import { hasWarehouseEditRole } from '../constants/roles.constants';
import {
  normalizeWarehousePieceLabel,
  WAREHOUSE_PIECE_LABEL_MAX_LENGTH,
} from './warehouse-piece-label.util';

/** Max. délka čísla šarže (stejný limit jako označení kusu). */
export const WAREHOUSE_BATCH_NUMBER_MAX_LENGTH = WAREHOUSE_PIECE_LABEL_MAX_LENGTH;

/** Trim + collapse whitespace; prázdné → null. */
export function normalizeWarehouseBatchNumber(raw: unknown): string | null {
  return normalizeWarehousePieceLabel(raw);
}

/**
 * Úprava šarže na kusu po naskladnění — stejné role jako označení
 * (pouze `sklad`).
 */
export function canEditWarehouseStockBatch(roles: string[] | undefined | null): boolean {
  return hasWarehouseEditRole(roles);
}

export function warehouseStockBatchExpandKey(groupId: string, batchNumber: string): string {
  return `${groupId}::${batchNumber}`;
}

export function groupWarehouseStockPiecesByBatch<T extends { batchNumber?: string | null }>(
  pieces: readonly T[],
): { batches: Array<{ batchNumber: string; pieces: T[] }>; unbatched: T[] } {
  const byBatch = new Map<string, T[]>();
  const unbatched: T[] = [];
  for (const piece of pieces) {
    const batch = normalizeWarehouseBatchNumber(piece.batchNumber);
    if (!batch) {
      unbatched.push(piece);
      continue;
    }
    const list = byBatch.get(batch) ?? [];
    list.push(piece);
    byBatch.set(batch, list);
  }
  const batches = [...byBatch.entries()]
    .map(([batchNumber, batchPieces]) => ({ batchNumber, pieces: batchPieces }))
    .sort((a, b) => a.batchNumber.localeCompare(b.batchNumber, 'cs'));
  return { batches, unbatched };
}

export interface WarehouseStockExpandGroup<T> {
  id: string;
  expandable: boolean;
  pieces: T[];
}

export type WarehouseStockExpandRow<T> =
  | { kind: 'product'; groupId: string }
  | { kind: 'batch'; groupId: string; batchKey: string; batchNumber: string; pieces: T[] }
  | { kind: 'piece'; groupId: string; piece: T; nested: boolean };

/**
 * Flatten zásob: produkt vždy; šarže / kusy jen když je produkt rozbalený.
 * Šarže jsou samy o sobě sbalené, dokud není `expandedBatchKeys`.
 */
export function buildWarehouseStockExpandRows<T extends { batchNumber?: string | null }>(
  groups: readonly WarehouseStockExpandGroup<T>[],
  expandedProductIds: ReadonlySet<string>,
  expandedBatchKeys: ReadonlySet<string>,
): WarehouseStockExpandRow<T>[] {
  const rows: WarehouseStockExpandRow<T>[] = [];
  for (const group of groups) {
    rows.push({ kind: 'product', groupId: group.id });
    if (!group.expandable || !expandedProductIds.has(group.id)) continue;

    const { batches, unbatched } = groupWarehouseStockPiecesByBatch(group.pieces);
    if (batches.length === 0) {
      for (const piece of group.pieces) {
        rows.push({ kind: 'piece', groupId: group.id, piece, nested: false });
      }
      continue;
    }

    for (const batch of batches) {
      const batchKey = warehouseStockBatchExpandKey(group.id, batch.batchNumber);
      rows.push({
        kind: 'batch',
        groupId: group.id,
        batchKey,
        batchNumber: batch.batchNumber,
        pieces: batch.pieces,
      });
      if (!expandedBatchKeys.has(batchKey)) continue;
      for (const piece of batch.pieces) {
        rows.push({ kind: 'piece', groupId: group.id, piece, nested: true });
      }
    }
    for (const piece of unbatched) {
      rows.push({ kind: 'piece', groupId: group.id, piece, nested: false });
    }
  }
  return rows;
}
